import dayjs from 'dayjs';

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

/**
 * ForecastCalculator - Handles all complex forecast calculations
 * Preserves all existing business logic while organizing it better
 */
export class ForecastCalculator {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Calculate company breakdown from staffing data with monthly details
   */
  calculateCompanyBreakdown(staffingData) {
    const companyBreakdown = {};
    const fixedCostBreakdown = {};
    
    // Helper to initialize company structure
    const initializeCompany = (company) => {
      if (!companyBreakdown[company]) {
        companyBreakdown[company] = {
          monthlyForecast: Array(12).fill(0),
          monthlyCapex: Array(12).fill(0),
          monthlyOpex: Array(12).fill(0)
        };
      }
    };
    
    // Helper to initialize fixed cost structure
    const initializeFixedCost = (costName) => {
      if (!fixedCostBreakdown[costName]) {
        fixedCostBreakdown[costName] = {
          monthlyForecast: Array(12).fill(0),
          monthlyCapex: Array(12).fill(0),
          monthlyOpex: Array(12).fill(0)
        };
      }
    };
    
    // Process milestones and roles
    if (staffingData.milestones) {
      staffingData.milestones.forEach((milestone, milestoneIndex) => {
        if (milestone.roles) {
          milestone.roles.forEach((role, roleIndex) => {
            const company = role.company || 'Unknown';
            initializeCompany(company);
            
            const monthlyRate = role.rate || 0;
            const baseAllocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
            
            // Process dateRanges with dual format support
            if (role.dateRanges && role.dateRanges.length > 0) {
              role.dateRanges.forEach((range, rangeIndex) => {
                let startMonth, endMonth;
                
                const isCalendarFormat = (
                  range.timelineVersion === '3.0' ||
                  (range.startMonth >= 1 && range.startMonth <= 12 && 
                   range.endMonth >= 1 && range.endMonth <= 12 &&
                   range.startMonth <= range.endMonth && 
                   !range.useProjectRelative)
                );
                
                if (isCalendarFormat) {
                  startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
                  endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
                } else {
                  startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
                  endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
                  
                }
                
                const rangeAllocation = Math.max(0, Math.min(100, range.allocation || 0)) / 100;
                
                for (let month = startMonth; month <= endMonth; month++) {
                  const monthIndex = month - 1;
                  if (monthIndex >= 0 && monthIndex < 12) {
                    const monthlyCost = monthlyRate * rangeAllocation;
                    
                    if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                      companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
                      
                      if (role.capex_classification === 'CAPEX') {
                        companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                      } else {
                        companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                      }
                    }
                  }
                }
              });
            } else {
              const startMonth = role.milestone_start_month || 1;
              const endMonth = role.milestone_end_month || 12;
              const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
              
              for (let month = startMonth; month <= endMonth; month++) {
                const monthIndex = month - 1;
                if (monthIndex >= 0 && monthIndex < 12) {
                  const monthlyCost = monthlyRate * allocation;
                  
                  if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                    companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
                    
                    if (role.capex_classification === 'CAPEX') {
                      companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                    } else {
                      companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                    }
                  }
                }
              }
            }
          });
        }
      });
    }
    
    // Add fixed costs
    if (staffingData.fixedCosts) {
      staffingData.fixedCosts.forEach((cost, costIndex) => {
        const costName = cost.type || cost.name || `Fixed Cost ${costIndex + 1}`;
        const company = (cost.company && typeof cost.company === 'object'
          ? cost.company.name
          : cost.company) || 'Other';
        
        initializeCompany(company);
        initializeFixedCost(costName);
        
        // Store the details, company, and department tags
        fixedCostBreakdown[costName].details = cost.details || '';
        fixedCostBreakdown[costName].company = company;
        fixedCostBreakdown[costName].departments = (cost.departments || []).map(d =>
          typeof d === 'object' ? d.name : d
        );
        
        let startDate, endDate;
        try {
          startDate = dayjs(cost.start_date + '-01');
          endDate = dayjs(cost.end_date + '-01');
          
          if (!startDate.isValid() || !endDate.isValid()) {
            console.warn(`Invalid dates for fixed cost ${costIndex}:`, cost.start_date, cost.end_date);
            return;
          }
        } catch (error) {
          console.warn(`Error parsing dates for fixed cost ${costIndex}:`, error);
          return;
        }
        
        const monthlyCost = cost.monthlyCost || cost.monthly_cost || 0;
        const startMonth = Math.max(1, Math.min(12, startDate.month() + 1));
        const endMonth = Math.max(startMonth, Math.min(12, endDate.month() + 1));
        
        if (!isNaN(monthlyCost) && monthlyCost >= 0) {
          for (let month = startMonth; month <= endMonth; month++) {
            const monthIndex = month - 1;
            if (monthIndex >= 0 && monthIndex < 12) {
              // Add to company breakdown (this will be used for totals)
              companyBreakdown[company].monthlyForecast[monthIndex] += monthlyCost;
              
              // Also track in fixed cost breakdown for detailed reporting
              fixedCostBreakdown[costName].monthlyForecast[monthIndex] += monthlyCost;
              
              if ((cost.capex_classification || 'OPEX') === 'CAPEX') {
                companyBreakdown[company].monthlyCapex[monthIndex] += monthlyCost;
                fixedCostBreakdown[costName].monthlyCapex[monthIndex] += monthlyCost;
              } else {
                companyBreakdown[company].monthlyOpex[monthIndex] += monthlyCost;
                fixedCostBreakdown[costName].monthlyOpex[monthIndex] += monthlyCost;
              }
            }
          }
        }
      });
    }
    
    // Calculate totals from monthly arrays
    Object.keys(companyBreakdown).forEach(company => {
      const companyData = companyBreakdown[company];
      companyData.capex = companyData.monthlyCapex.reduce((sum, val) => sum + val, 0);
      companyData.opex = companyData.monthlyOpex.reduce((sum, val) => sum + val, 0);
      companyData.total = companyData.monthlyForecast.reduce((sum, val) => sum + val, 0);
    });
    
    Object.keys(fixedCostBreakdown).forEach(costName => {
      const costData = fixedCostBreakdown[costName];
      costData.capex = costData.monthlyCapex.reduce((sum, val) => sum + val, 0);
      costData.opex = costData.monthlyOpex.reduce((sum, val) => sum + val, 0);
      costData.total = costData.monthlyForecast.reduce((sum, val) => sum + val, 0);
      
      // Ensure details and company are preserved from the original data
      if (!costData.details && !costData.company) {
        // Try to find the original cost data to get details
        const originalCost = staffingData.fixedCosts?.find(cost => 
          (cost.type || cost.name || `Fixed Cost ${staffingData.fixedCosts.indexOf(cost) + 1}`) === costName
        );
        if (originalCost) {
          costData.details = originalCost.details || '';
          costData.company = originalCost.company || 'Other';
        }
      }
    });
    
    return { companyBreakdown, fixedCostBreakdown };
  }

  /**
   * Calculate department breakdown from staffing data — mirrors calculateCompanyBreakdown
   * but groups by role.department instead of role.company.
   * Fixed costs are not dept-attributed so they are excluded here.
   */
  calculateDepartmentBreakdown(staffingData) {
    const deptBreakdown = {};
    // Cross-cut: { dept: { company: { total, capex, opex, monthlyForecast[] } } }
    const deptCompanyBreakdown = {};

    const initializeDept = (dept) => {
      if (!deptBreakdown[dept]) {
        deptBreakdown[dept] = {
          monthlyForecast: Array(12).fill(0),
          monthlyCapex: Array(12).fill(0),
          monthlyOpex: Array(12).fill(0)
        };
      }
    };

    const initializeDeptCompany = (dept, company) => {
      if (!deptCompanyBreakdown[dept]) deptCompanyBreakdown[dept] = {};
      if (!deptCompanyBreakdown[dept][company]) {
        deptCompanyBreakdown[dept][company] = {
          monthlyForecast: Array(12).fill(0),
          capex: 0, opex: 0, total: 0
        };
      }
    };

    if (staffingData.milestones) {
      staffingData.milestones.forEach((milestone) => {
        if (milestone.roles) {
          milestone.roles.forEach((role) => {
            const dept = (typeof role.department === 'object' && role.department !== null
              ? role.department.name
              : role.department) || 'Unassigned';
            const company = role.company || 'Unknown';
            initializeDept(dept);
            initializeDeptCompany(dept, company);

            const monthlyRate = role.rate || 0;
            const baseAllocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
            const isCapex = role.capex_classification === 'CAPEX';

            if (role.dateRanges && role.dateRanges.length > 0) {
              role.dateRanges.forEach((range) => {
                const startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
                const endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
                const rangeAllocation = Math.max(0, Math.min(100, range.allocation || 0)) / 100;

                for (let month = startMonth; month <= endMonth; month++) {
                  const monthIndex = month - 1;
                  if (monthIndex >= 0 && monthIndex < 12) {
                    const monthlyCost = monthlyRate * rangeAllocation;
                    if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                      deptBreakdown[dept].monthlyForecast[monthIndex] += monthlyCost;
                      deptCompanyBreakdown[dept][company].monthlyForecast[monthIndex] += monthlyCost;
                      if (isCapex) {
                        deptBreakdown[dept].monthlyCapex[monthIndex] += monthlyCost;
                      } else {
                        deptBreakdown[dept].monthlyOpex[monthIndex] += monthlyCost;
                      }
                    }
                  }
                }
              });
            } else {
              const startMonth = role.milestone_start_month || 1;
              const endMonth = role.milestone_end_month || 12;
              for (let month = startMonth; month <= endMonth; month++) {
                const monthIndex = month - 1;
                if (monthIndex >= 0 && monthIndex < 12) {
                  const monthlyCost = monthlyRate * baseAllocation;
                  if (!isNaN(monthlyCost) && monthlyCost >= 0) {
                    deptBreakdown[dept].monthlyForecast[monthIndex] += monthlyCost;
                    deptCompanyBreakdown[dept][company].monthlyForecast[monthIndex] += monthlyCost;
                    if (isCapex) {
                      deptBreakdown[dept].monthlyCapex[monthIndex] += monthlyCost;
                    } else {
                      deptBreakdown[dept].monthlyOpex[monthIndex] += monthlyCost;
                    }
                  }
                }
              }
            }
          });
        }
      });
    }

    Object.keys(deptBreakdown).forEach(dept => {
      const d = deptBreakdown[dept];
      d.capex = d.monthlyCapex.reduce((sum, val) => sum + val, 0);
      d.opex = d.monthlyOpex.reduce((sum, val) => sum + val, 0);
      d.total = d.monthlyForecast.reduce((sum, val) => sum + val, 0);
    });

    Object.keys(deptCompanyBreakdown).forEach(dept => {
      Object.keys(deptCompanyBreakdown[dept]).forEach(company => {
        const dc = deptCompanyBreakdown[dept][company];
        dc.total = dc.monthlyForecast.reduce((s, v) => s + v, 0);
        // Capex/opex ratio from parent dept breakdown (same classification)
        const deptTotal = deptBreakdown[dept]?.total || 0;
        const capexRatio = deptTotal > 0 ? (deptBreakdown[dept]?.capex || 0) / deptTotal : 0;
        dc.capex = dc.total * capexRatio;
        dc.opex = dc.total * (1 - capexRatio);
      });
    });

    return { deptBreakdown, deptCompanyBreakdown };
  }

  /**
   * Calculate enhanced spend status with multi-tier assessment
   */
  calculateSpendStatus(outcome) {
    const {
      totalProjectedCost,
      latestActualSpendMonth,
      monthlyActual,
      monthlyProjected,
      department_budgets,
    } = outcome;

    const approvedBudget = (department_budgets || []).reduce(
      (s, db) => s + (db.capex || 0) + (db.opex || 0), 0
    );

    let baselineForecastThroughActualMonth = 0;
    let actualSpendThroughLatestMonth = 0;

    if (latestActualSpendMonth >= 0) {
      actualSpendThroughLatestMonth = (monthlyActual || [])
        .slice(0, latestActualSpendMonth + 1)
        .reduce((sum, v) => sum + (v || 0), 0);
      baselineForecastThroughActualMonth = this.getCumulativeForecastThroughMonth(
        monthlyProjected || [],
        latestActualSpendMonth
      );
    }

    const projectedOverBudget = approvedBudget > 0 && totalProjectedCost > approvedBudget;
    const ytdActualOverProjected = actualSpendThroughLatestMonth > 0 &&
      baselineForecastThroughActualMonth > 0 &&
      actualSpendThroughLatestMonth > baselineForecastThroughActualMonth;

    let spendStatus, spendStatusText;
    if (projectedOverBudget && ytdActualOverProjected) {
      spendStatus = 'needs-attention';
      spendStatusText = 'Needs Attention';
    } else if (!projectedOverBudget && ytdActualOverProjected) {
      spendStatus = 'at-risk';
      spendStatusText = 'At Risk';
    } else if (projectedOverBudget) {
      spendStatus = 'off-track';
      spendStatusText = 'Off Track';
    } else {
      spendStatus = 'on-track';
      spendStatusText = 'On Track';
    }

    return {
      spendStatus,
      spendStatusText,
      baselineForecastThroughActualMonth,
      actualSpendThroughLatestMonth
    };
  }

  /**
   * Calculate cumulative forecast through a specific month
   * PRESERVES EXISTING HELPER LOGIC
   */
  getCumulativeForecastThroughMonth(monthlyForecast, throughMonth) {
    if (throughMonth < 0) return 0;
    return monthlyForecast.slice(0, throughMonth + 1).reduce((sum, amount) => sum + (amount || 0), 0);
  }

  /**
   * Find the latest month with actual spend data
   * PRESERVES EXISTING HELPER LOGIC
   */
  getLatestActualSpendMonth(monthlyActual) {
    let latestMonth = -1;
    for (let i = 11; i >= 0; i--) {
      if (monthlyActual[i] !== null && monthlyActual[i] > 0) {
        latestMonth = i;
        break;
      }
    }
    return latestMonth;
  }

  /**
   * Distribute actual spend by company based on forecast proportions
   */
  distributeActualSpendByCompany(companyBreakdown, actualSpendData, totalForecast) {
    const companyActuals = {};
    
    const monthlyActual = Array(12).fill(null);
    if (actualSpendData.monthlySpend) {
      Object.entries(actualSpendData.monthlySpend).forEach(([month, amount]) => {
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyActual[monthIndex] = amount;
        }
      });
    }
    
    const totalActualSpend = (actualSpendData.hasUploadedData && actualSpendData.totalActual !== undefined) ? 
      actualSpendData.totalActual : 
      monthlyActual.filter(amount => amount !== null).reduce((sum, amount) => sum + amount, 0);
    
    if (actualSpendData.hasUploadedData && actualSpendData.companySpend) {
      Object.entries(actualSpendData.companySpend).forEach(([company, spendData]) => {
        companyActuals[company] = {
          capex: spendData.capex || 0,
          opex: spendData.opex || 0,
          total: spendData.total || ((spendData.capex || 0) + (spendData.opex || 0))
        };
      });
    } else if (totalActualSpend > 0 && totalForecast > 0) {
      Object.entries(companyBreakdown).forEach(([company, forecast]) => {
        const companyProportion = forecast.total / totalForecast;
        const companyActualTotal = totalActualSpend * companyProportion;
        
        const capexProportion = forecast.total > 0 ? forecast.capex / forecast.total : 0;
        const opexProportion = forecast.total > 0 ? forecast.opex / forecast.total : 1;
        
        companyActuals[company] = {
          capex: companyActualTotal * capexProportion,
          opex: companyActualTotal * opexProportion,
          total: companyActualTotal
        };
      });
    }
    
    return companyActuals;
  }

  /**
   * Calculate plan totals with enhanced variance tracking
   */
  calculatePlanTotals(filteredOutcomes, selectedDept = null) {
    const result = filteredOutcomes.reduce((totals, outcome) => {
      let actualCapex = outcome.totalActualCapex || 0;
      let actualOpex = outcome.totalActualOpex || 0;

      if (actualCapex === 0 && actualOpex === 0 && outcome.totalActualSpend > 0) {
        if (outcome.companyActuals && Object.keys(outcome.companyActuals).length > 0) {
          actualCapex = Object.values(outcome.companyActuals).reduce((sum, company) => sum + (company.capex || 0), 0);
          actualOpex = Object.values(outcome.companyActuals).reduce((sum, company) => sum + (company.opex || 0), 0);
        }
      }

      // When a dept filter is active, scope staff forecast to that dept's breakdown
      const deptData = selectedDept ? (outcome.deptBreakdown?.[selectedDept] || null) : null;
      const deptProjData = selectedDept ? (outcome.deptProjected?.[selectedDept] || null) : null;
      const deptBudgetRow = selectedDept
        ? ((outcome.department_budgets || []).find(db => db.department_name === selectedDept) || null)
        : null;

      const staffForecastCapex = deptData
        ? (deptData.capex || 0)
        : Object.values(outcome.companyBreakdown || {}).reduce((sum, company) => sum + (company.capex || 0), 0);
      const staffForecastOpex = deptData
        ? (deptData.opex || 0)
        : Object.values(outcome.companyBreakdown || {}).reduce((sum, company) => sum + (company.opex || 0), 0);
      const staffActualCapex = Object.values(outcome.companyActuals || {}).reduce((sum, company) => sum + (company.capex || 0), 0);
      const staffActualOpex = Object.values(outcome.companyActuals || {}).reduce((sum, company) => sum + (company.opex || 0), 0);

      // Fixed costs: include all when no dept filter; when dept filter active, include only
      // costs tagged to that dept (or costs with no dept tags, which are unattributed).
      const fixedCostFilter = (fc) => {
        if (!selectedDept) return true;
        const fcDepts = fc.departments || [];
        return fcDepts.length === 0 || fcDepts.includes(selectedDept);
      };
      const fixedForecastCapex = Object.values(outcome.fixedCostBreakdown || {}).reduce((sum, fc) => fixedCostFilter(fc) ? sum + (fc.capex || 0) : sum, 0);
      const fixedForecastOpex  = Object.values(outcome.fixedCostBreakdown || {}).reduce((sum, fc) => fixedCostFilter(fc) ? sum + (fc.opex  || 0) : sum, 0);
      const fixedActualCapex   = Object.values(outcome.fixedCostActuals   || {}).reduce((sum, fc) => fixedCostFilter(fc) ? sum + (fc.capex || 0) : sum, 0);
      const fixedActualOpex    = Object.values(outcome.fixedCostActuals   || {}).reduce((sum, fc) => fixedCostFilter(fc) ? sum + (fc.opex  || 0) : sum, 0);

      // YTD calculation — sum monthly data up through the latest month with actuals
      const outcomeCutoff = outcome.latestActualSpendMonth ?? -1;
      const monthlyActualArr = outcome.monthlyActual || [];
      const monthlyProjectedArr = outcome.monthlyProjected || [];
      const monthlyOfficialForecastArr = outcome.monthlyOfficialForecast || [];
      // When dept filter active, prefer dept-scoped monthly projected data
      const deptMonthlyProjected = selectedDept
        ? (outcome.deptProjected?.[selectedDept]?.monthlyProjected || null)
        : null;
      let ytdActual = 0;
      let ytdProjected = 0;
      let ytdOfficialForecast = 0;
      if (outcomeCutoff >= 0) {
        for (let i = 0; i <= outcomeCutoff; i++) {
          ytdActual += monthlyActualArr[i] || 0;
          ytdProjected += deptMonthlyProjected
            ? (deptMonthlyProjected[i] || 0)
            : (monthlyProjectedArr[i] || 0);
          ytdOfficialForecast += monthlyOfficialForecastArr[i] || 0;
        }
      }

      // EOY landing estimate: actuals through cutoff, projected for remainder
      let eoyEstimate = 0;
      let ppmEoyEstimate = 0;
      const eoyMonthlyArr = outcome.monthlyActual || [];
      const eoyProjArr = deptMonthlyProjected || monthlyProjectedArr;
      const ppmMonthlyProjectedArr = outcome.monthlyStaffingProjected || monthlyProjectedArr;
      for (let i = 0; i < 12; i++) {
        if (i <= outcomeCutoff && outcomeCutoff >= 0) {
          eoyEstimate += eoyMonthlyArr[i] || 0;
          ppmEoyEstimate += eoyMonthlyArr[i] || 0;
        } else {
          eoyEstimate += eoyProjArr[i] || 0;
          ppmEoyEstimate += ppmMonthlyProjectedArr[i] || 0;
        }
      }

      // Full 12-month plan arrays for tooltip breakdowns
      const planMonthlyActualUpdate = totals.planMonthlyActual.map(
        (v, i) => v + (monthlyActualArr[i] || 0)
      );
      const planMonthlyProjectedUpdate = totals.planMonthlyProjected.map(
        (v, i) => v + (deptMonthlyProjected ? (deptMonthlyProjected[i] || 0) : (monthlyProjectedArr[i] || 0))
      );
      const planMonthlyOfficialForecastUpdate = totals.planMonthlyOfficialForecast.map(
        (v, i) => v + (monthlyOfficialForecastArr[i] || 0)
      );

      // Calculate individual outcome variances for aggregation
      const deptBudgetCapex = deptBudgetRow ? (deptBudgetRow.capex || 0) : null;
      const deptBudgetOpex  = deptBudgetRow ? (deptBudgetRow.opex  || 0) : null;
      const allDeptsCapex = (outcome.department_budgets || []).reduce((s, db) => s + (db.capex || 0), 0);
      const allDeptsOpex  = (outcome.department_budgets || []).reduce((s, db) => s + (db.opex  || 0), 0);
      const approvedCapex = deptBudgetCapex !== null ? deptBudgetCapex
        : (allDeptsCapex > 0 ? allDeptsCapex : (outcome.approved_budget?.capex || 0));
      const approvedOpex  = deptBudgetOpex  !== null ? deptBudgetOpex
        : (allDeptsOpex  > 0 ? allDeptsOpex  : (outcome.approved_budget?.opex  || 0));
      const approvedTotal = approvedCapex + approvedOpex;
      const projectedTotal = deptProjData ? (deptProjData.totalProjectedCost || 0) : (outcome.totalProjectedCost || 0);
      const forecastCapex = deptProjData ? (deptProjData.totalCapex || 0) : (outcome.totalCapex || 0);
      const forecastOpex = deptProjData ? (deptProjData.totalOpex || 0) : (outcome.totalOpex || 0);
      const officialTotal = outcome.totalOfficialForecast || 0;
      const actualTotal = outcome.totalActualSpend || 0;

      // Budget compliance variance (projected vs approved)
      const budgetVariance = approvedTotal > 0 ? projectedTotal - approvedTotal : 0;

      // Execution variance (actual vs baseline forecast)
      const baselineForecast = officialTotal > 0 ? officialTotal : projectedTotal;
      const executionVariance = actualTotal > 0 && baselineForecast > 0 ? actualTotal - baselineForecast : 0;

      // Forecast alignment variance (official vs projected)
      const forecastVariance = officialTotal > 0 && projectedTotal > 0 ? officialTotal - projectedTotal : 0;

      return {
        totalApprovedCapex: totals.totalApprovedCapex + approvedCapex,
        totalApprovedOpex: totals.totalApprovedOpex + approvedOpex,
        totalApprovedBudget: totals.totalApprovedBudget + approvedTotal,
        totalForecastCapex: totals.totalForecastCapex + forecastCapex,
        totalForecastOpex: totals.totalForecastOpex + forecastOpex,
        totalForecastBudget: totals.totalForecastBudget + projectedTotal,
        totalOfficialForecastCapex: totals.totalOfficialForecastCapex + (outcome.totalOfficialCapex || 0),
        totalOfficialForecastOpex: totals.totalOfficialForecastOpex + (outcome.totalOfficialOpex || 0),
        totalOfficialForecastBudget: totals.totalOfficialForecastBudget + officialTotal,
        totalActualCapex: totals.totalActualCapex + actualCapex,
        totalActualOpex: totals.totalActualOpex + actualOpex,
        totalActualBudget: totals.totalActualBudget + actualTotal,
        totalStaffForecastCapex: totals.totalStaffForecastCapex + staffForecastCapex,
        totalStaffForecastOpex: totals.totalStaffForecastOpex + staffForecastOpex,
        totalStaffForecastBudget: totals.totalStaffForecastBudget + staffForecastCapex + staffForecastOpex,
        totalStaffActualCapex: totals.totalStaffActualCapex + actualCapex,
        totalStaffActualOpex: totals.totalStaffActualOpex + actualOpex,
        totalStaffActualBudget: totals.totalStaffActualBudget + actualCapex + actualOpex,
        totalFixedForecastCapex: totals.totalFixedForecastCapex + fixedForecastCapex,
        totalFixedForecastOpex: totals.totalFixedForecastOpex + fixedForecastOpex,
        totalFixedForecastBudget: totals.totalFixedForecastBudget + fixedForecastCapex + fixedForecastOpex,
        totalFixedActualCapex: totals.totalFixedActualCapex + fixedActualCapex,
        totalFixedActualOpex: totals.totalFixedActualOpex + fixedActualOpex,
        totalFixedActualBudget: totals.totalFixedActualBudget + fixedActualCapex + fixedActualOpex,
        // Aggregated variance tracking
        totalBudgetVariance: totals.totalBudgetVariance + budgetVariance,
        totalExecutionVariance: totals.totalExecutionVariance + executionVariance,
        totalForecastVariance: totals.totalForecastVariance + forecastVariance,
        // Outcome counts for analysis
        outcomesWithApprovedBudget: totals.outcomesWithApprovedBudget + (approvedTotal > 0 ? 1 : 0),
        outcomesWithProjectedForecast: totals.outcomesWithProjectedForecast + (projectedTotal > 0 ? 1 : 0),
        outcomesWithOfficialForecast: totals.outcomesWithOfficialForecast + (officialTotal > 0 ? 1 : 0),
        outcomesWithActualSpend: totals.outcomesWithActualSpend + (actualTotal > 0 ? 1 : 0),
        outcomesOverBudget: totals.outcomesOverBudget + (approvedTotal > 0 && budgetVariance > approvedTotal * 0.05 ? 1 : 0),
        outcomesOverTarget: totals.outcomesOverTarget + (baselineForecast > 0 && executionVariance > baselineForecast * 0.1 ? 1 : 0),
        // EOY landing estimate
        eoyLandingEstimate: totals.eoyLandingEstimate + eoyEstimate,
        ppmEoyLandingEstimate: totals.ppmEoyLandingEstimate + ppmEoyEstimate,
        // YTD totals
        ytdActualBudget: totals.ytdActualBudget + ytdActual,
        ytdProjectedBudget: totals.ytdProjectedBudget + ytdProjected,
        ytdOfficialForecastBudget: totals.ytdOfficialForecastBudget + ytdOfficialForecast,
        ytdCutoffMonth: Math.max(totals.ytdCutoffMonth, outcomeCutoff),
        // Monthly plan arrays for tooltip breakdowns
        planMonthlyActual: planMonthlyActualUpdate,
        planMonthlyProjected: planMonthlyProjectedUpdate,
        planMonthlyOfficialForecast: planMonthlyOfficialForecastUpdate,
      };
    }, {
      totalApprovedCapex: 0,
      totalApprovedOpex: 0,
      totalApprovedBudget: 0,
      totalForecastCapex: 0,
      totalForecastOpex: 0,
      totalForecastBudget: 0,
      totalOfficialForecastCapex: 0,
      totalOfficialForecastOpex: 0,
      totalOfficialForecastBudget: 0,
      totalActualCapex: 0,
      totalActualOpex: 0,
      totalActualBudget: 0,
      totalStaffForecastCapex: 0,
      totalStaffForecastOpex: 0,
      totalStaffForecastBudget: 0,
      totalStaffActualCapex: 0,
      totalStaffActualOpex: 0,
      totalStaffActualBudget: 0,
      totalFixedForecastCapex: 0,
      totalFixedForecastOpex: 0,
      totalFixedForecastBudget: 0,
      totalFixedActualCapex: 0,
      totalFixedActualOpex: 0,
      totalFixedActualBudget: 0,
      // EOY landing estimate
      eoyLandingEstimate: 0,
      ppmEoyLandingEstimate: 0,
      // Variance tracking
      totalBudgetVariance: 0,
      totalExecutionVariance: 0,
      totalForecastVariance: 0,
      // Outcome counts
      outcomesWithApprovedBudget: 0,
      // YTD totals
      ytdActualBudget: 0,
      ytdProjectedBudget: 0,
      ytdOfficialForecastBudget: 0,
      ytdCutoffMonth: -1,
      // Monthly plan arrays
      planMonthlyActual: new Array(12).fill(0),
      planMonthlyProjected: new Array(12).fill(0),
      planMonthlyOfficialForecast: new Array(12).fill(0),
      outcomesWithProjectedForecast: 0,
      outcomesWithOfficialForecast: 0,
      outcomesWithActualSpend: 0,
      outcomesOverBudget: 0,
      outcomesOverTarget: 0
    });
    
    return result;
  }
}

// Utility functions that were in the component
export const formatCurrencyFull = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export const formatCurrency = (amount) => {
  const v = amount || 0;
  if (Math.abs(v) >= 1_000_000) {
    return `$${(v / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(v) >= 1_000) {
    return `$${Math.round(v / 1_000)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

export const formatPercentage = (value) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

export { MONTHS };